import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatDropdownOpen(false);
    setGuideDropdownOpen(false);
  }, [location]);

  const linkClass = (scrolled: boolean) =>
    `text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
      scrolled
        ? 'text-foreground-700 hover:text-primary-600'
        : 'text-background-50/90 hover:text-background-50'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-9 md:h-9 bg-primary-500 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <i className="ri-layout-grid-line text-background-50 text-base md:text-lg w-4 h-4 flex items-center justify-center"></i>
            </div>
            <span
              className={`font-heading text-lg md:text-xl font-bold transition-colors duration-300 ${
                scrolled ? 'text-foreground-900' : 'text-background-50'
              }`}
            >
              GTBuy
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={linkClass(scrolled)}>
              Home
            </Link>

            <div className="relative" ref={catRef}>
              <button
                onClick={() => {
                  setCatDropdownOpen(!catDropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 ${linkClass(scrolled)}`}
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 w-3.5 h-3.5 flex items-center justify-center ${
                    catDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {catDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[560px] bg-background-50 rounded-2xl shadow-lg border border-background-200/70 p-5 animate-in">
                  <p className="text-xs text-foreground-400 font-medium uppercase tracking-wider mb-3 px-1">
                    Shop by Category
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="group flex flex-col items-center gap-2 p-2.5 rounded-xl hover:bg-background-100 transition-colors duration-150"
                      >
                        <div className="w-14 h-14 rounded-lg overflow-hidden">
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-xs text-foreground-700 text-center leading-tight group-hover:text-primary-600 transition-colors whitespace-nowrap">
                          {cat.name}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setCatDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 ${linkClass(scrolled)}`}
              >
                Shopping Guides
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 w-3.5 h-3.5 flex items-center justify-center ${
                    guideDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full right-0 mt-3 w-80 bg-background-50 rounded-2xl shadow-lg border border-background-200/70 py-3 animate-in">
                  <p className="text-xs text-foreground-400 font-medium uppercase tracking-wider px-4 mb-2">
                    SEO Guides
                  </p>
                  <div className="max-h-72 overflow-y-auto">
                    {articles.map((article) => (
                      <Link
                        key={article.slug}
                        to={article.slug}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                      >
                        {article.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
              scrolled ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            <i
              className={`text-xl w-5 h-5 flex items-center justify-center ${
                mobileOpen ? 'ri-close-line' : 'ri-menu-line'
              }`}
            ></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3">
            <Link
              to="/"
              className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-background-100 transition-colors"
            >
              <i className="ri-home-line text-foreground-600 text-lg w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-foreground-900">Home</span>
            </Link>

            <div className="py-3">
              <p className="text-xs text-foreground-400 font-semibold uppercase tracking-wider px-2 mb-2">
                Categories
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs text-foreground-700 leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="py-3 border-t border-background-200/70">
              <p className="text-xs text-foreground-400 font-semibold uppercase tracking-wider px-2 mb-2">
                Shopping Guides
              </p>
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={article.slug}
                  className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors"
                >
                  <i className="ri-article-line text-foreground-500 text-base w-4.5 h-4.5 flex items-center justify-center"></i>
                  <span className="text-sm text-foreground-700 leading-snug">{article.title}</span>
                </Link>
              ))}
            </div>

            <div className="py-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-sm w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}